import { CssBaseline, ThemeProvider } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { HashRouter } from 'react-router-dom'
import { darkTheme, lightTheme } from './Theme'
import { axiosClient } from './api'
import ServerSelector from './components/ServerSelector'
import { SELECTED_SERVER_STORAGE_KEY, saveStoredBackendOptions } from './config/server-options'
import { STORAGE_KEYS } from './constants'
import { useAppSelector } from './redux-store/hooks'
import { AppRoutes } from './routes'
import NetworkRecoveryDialog from './components/NetworkRecoveryDialog'

const FONT_SIZES = [10, 11, 12, 13, 14, 16, 18, 20, 22, 24, 26, 28, 30]
// Bumped default to 18px (index 6 -> 7) so type is more readable on the
// counter screen out of the box. The +/- header buttons let users go back
// down if they prefer denser layouts.
const DEFAULT_FONT_INDEX = 7
// One-time migration: users whose only stored value is the previous
// 14px default get bumped along with everyone else. Anyone who
// explicitly chose another size keeps their preference.
const PREVIOUS_DEFAULT_FONT_INDEX = 5
const FONT_MIGRATION_KEY = 'fontSizeIndexMigrated'

const isNetworkRequestError = (error: any) => {
  if (error?.code === 'ERR_CANCELED') return false
  if (!navigator.onLine) return true
  if (error?.message === 'Network Error') return true
  if (['ERR_NETWORK', 'ECONNABORTED'].includes(error?.code)) return true
  return Boolean(error?.request && !error?.response)
}

const App: React.FC = () => {
  const [isLoaderActive, setIsLoaderActive] = useState(false)
  const [networkRecoveryOpen, setNetworkRecoveryOpen] = useState(false)

  // Single source of truth: localStorage. Never changes during a session —
  // server selection triggers a full page reload so everything re-initialises.
  const selectedServerUrl = localStorage.getItem(SELECTED_SERVER_STORAGE_KEY)

  const backendOptions = useAppSelector((state) => state.clientInfo.data.backendOptions)
  const activeRequests = useRef(0)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('theme') === 'dark'
  })
  const [fontSizeIndex, setFontSizeIndex] = useState(() => {
    const stored = localStorage.getItem('fontSizeIndex')
    const migrated = localStorage.getItem(FONT_MIGRATION_KEY) === '1'
    const parsed = stored != null ? parseInt(stored, 10) : NaN
    // First run on this build: if the user was on the old 14px default,
    // bump them to the new 18px default so they don't have to discover
    // the +/- button. Mark migration as done so this only happens once.
    if (!migrated) {
      localStorage.setItem(FONT_MIGRATION_KEY, '1')
      if (Number.isNaN(parsed) || parsed === PREVIOUS_DEFAULT_FONT_INDEX) {
        return DEFAULT_FONT_INDEX
      }
    }
    if (Number.isNaN(parsed)) return DEFAULT_FONT_INDEX
    return Math.min(Math.max(parsed, 0), FONT_SIZES.length - 1)
  })

  useEffect(() => {
    document.documentElement.style.fontSize = `${FONT_SIZES[fontSizeIndex]}px`
    localStorage.setItem('fontSizeIndex', String(fontSizeIndex))
  }, [fontSizeIndex])

  const increaseFontSize = () => setFontSizeIndex((i) => Math.min(i + 1, FONT_SIZES.length - 1))
  const decreaseFontSize = () => setFontSizeIndex((i) => Math.max(i - 1, 0))

  const toggleTheme = () => {
    setIsDarkMode((prevMode) => {
      const newMode = !prevMode
      localStorage.setItem('theme', newMode ? 'dark' : 'light')
      return newMode
    })
  }

  // Save URL and reload so axios, socket, and all state start clean.
  const handleServerSelect = (serverUrl: string) => {
    localStorage.setItem(SELECTED_SERVER_STORAGE_KEY, serverUrl)
    window.location.reload()
  }

  useEffect(() => {
    const shouldSkipLoader = (config: any) => Boolean(config?.skipGlobalLoader)

    const showLoader = () => {
      activeRequests.current += 1
      setIsLoaderActive(true)
    }

    const hideLoader = () => {
      activeRequests.current = Math.max(activeRequests.current - 1, 0)
      if (activeRequests.current === 0) {
        setIsLoaderActive(false)
      }
    }

    const requestInterceptor = axiosClient.interceptors.request.use(
      (config) => {
        if (!shouldSkipLoader(config)) showLoader()
        const token = localStorage.getItem(STORAGE_KEYS.TOKEN)
        config.headers.Authorization = `Bearer ${token}`
        return config
      },
      (error) => Promise.reject(error)
    )

    const responseInterceptor = axiosClient.interceptors.response.use(
      (response) => {
        if (!shouldSkipLoader(response.config)) hideLoader()
        return response
      },
      (error) => {
        if (!shouldSkipLoader(error?.config)) hideLoader()
        if (isNetworkRequestError(error)) {
          setNetworkRecoveryOpen(true)
        }
        return Promise.reject(error)
      }
    )

    const handleOffline = () => {
      setNetworkRecoveryOpen(true)
    }

    window.addEventListener('offline', handleOffline)

    return () => {
      axiosClient.interceptors.request.eject(requestInterceptor)
      axiosClient.interceptors.response.eject(responseInterceptor)
      window.removeEventListener('offline', handleOffline)
      activeRequests.current = 0
    }
  }, [])

  useEffect(() => {
    if (backendOptions?.length) {
      saveStoredBackendOptions(backendOptions)
    }
  }, [backendOptions])

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      {selectedServerUrl ? (
        <HashRouter>
          <AppRoutes
            isLoaderActive={isLoaderActive}
            toggleTheme={toggleTheme}
            increaseFontSize={increaseFontSize}
            decreaseFontSize={decreaseFontSize}
            canIncreaseFontSize={fontSizeIndex < FONT_SIZES.length - 1}
            canDecreaseFontSize={fontSizeIndex > 0}
            selectedServerUrl={selectedServerUrl}
            onServerChange={handleServerSelect}
          />
          <NetworkRecoveryDialog
            currentServerUrl={selectedServerUrl}
            onClose={() => setNetworkRecoveryOpen(false)}
            onSelectServer={handleServerSelect}
            open={networkRecoveryOpen}
          />
        </HashRouter>
      ) : (
        <ServerSelector onSelect={handleServerSelect} />
      )}
    </ThemeProvider>
  )
}

export default App
